import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";

const SUMMARY_LAYOUT_OPTIONS = [
  {
    value: "custom",
    label: "Patient, DOB, Prepared Date, Expiration Date",
  },
  {
    value: "default",
    label: "Patient, DOB, Payer, Service Date, Account, Services Provided",
  },
];

const FEE_TABLE_OPTIONS = [
  { value: "breakdownByProvider", label: "Breakdown by provider" },
  {
    value: "chargeDescription",
    label: "Charge description",
  },
];

const CONVENIENCE_SERVICE_FEES_OPTIONS = [
  { value: "showSeparately", label: "Show separately" },
  { value: "includeInSurgeonFees", label: "Include in surgeon fees" },
];

const DISCLAIMER_OPTIONS = [
  {
    value: "firstSentence",
    label: "Estimated surgery time only",
  },
  {
    value: "full",
    label: "Full disclaimer (Surgeon, Anesthesia, Facility fees)",
  },
];

const Title = styled.h3`
  margin-bottom: 16px;
`;

export function CustomPdfForm() {
  const {
    control,
    watch,
    formState: { errors },
    trigger,
  } = useFormContext<any>();
  const customPdfEnabledField = watch("customPdfEnabled");
  const feeTableLayout = watch("feeTableLayout");
  const showDisclaimer = watch("showDisclaimer");
  const showTermsAndConditions = watch("showTermsAndConditions");
  const { getFormErrorMessage, handleOnBlurField } = useFormValidation();

  return (
    <div className="block bg-white border-round p-4">
      <Title>Custom PDF</Title>
      <div className="formgrid grid">
        <div className="field col-4 flex flex-column">
          <Label htmlFor="customPdfEnabled">Enable custom PDF</Label>
          <Controller
            name="customPdfEnabled"
            control={control}
            render={({ field: { onChange, value } }) => (
              <InputSwitch
                id="customPdfEnabled"
                checked={value ?? false}
                onChange={onChange}
              />
            )}
          />
        </div>
      </div>
      {customPdfEnabledField && (
        <div className="formgrid grid">
          <div className="field col-12">
            <Label htmlFor="documentTitle">Document title</Label>
            <Controller
              name="documentTitle"
              control={control}
              rules={{
                required: "Document title is required.",
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <>
                  <InputText
                    id="documentTitle"
                    className="w-full"
                    value={value || ""}
                    placeholder="Enter document title"
                    onChange={onChange}
                    onBlur={() => {
                      handleOnBlurField({
                        onBlur,
                        field: "documentTitle",
                        trigger,
                      });
                    }}
                  />
                  {getFormErrorMessage("documentTitle", errors)}
                </>
              )}
            />
          </div>
          <div className="field col-12">
            <Label htmlFor="introductionText">Introduction text</Label>
            <Controller
              name="introductionText"
              control={control}
              rules={{
                required: "Introduction text is required.",
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <>
                  <InputTextarea
                    id="introductionText"
                    className="w-full"
                    value={value || ""}
                    rows={4}
                    placeholder="Enter introduction text. Use **text** for bold."
                    aria-labelledby="introductionText"
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={() => {
                      handleOnBlurField({
                        onBlur,
                        field: "introductionText",
                        trigger,
                      });
                    }}
                  />
                  <small className="text-color-secondary block mt-1">
                    Use **text** to make text bold (e.g. **important**)
                  </small>
                  {getFormErrorMessage("introductionText", errors)}
                </>
              )}
            />
          </div>
          <div className="field col-4">
            <Label htmlFor="summaryLayout">Summary layout</Label>
            <Controller
              name="summaryLayout"
              control={control}
              rules={{
                required: "Summary layout is required.",
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <>
                  <Dropdown
                    id="summaryLayout"
                    options={SUMMARY_LAYOUT_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    value={value}
                    onChange={onChange}
                    onBlur={() => {
                      handleOnBlurField({
                        onBlur,
                        field: "summaryLayout",
                        trigger,
                      });
                    }}
                    placeholder="Select"
                    className="w-full"
                  />
                  {getFormErrorMessage("summaryLayout", errors)}
                </>
              )}
            />
          </div>
          <div className="field col-4">
            <Label htmlFor="feeTableLayout">Fees as table</Label>
            <Controller
              name="feeTableLayout"
              control={control}
              rules={{
                required: "Fees as table is required.",
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <>
                  <Dropdown
                    id="feeTableLayout"
                    options={FEE_TABLE_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    value={value}
                    onChange={onChange}
                    onBlur={() => {
                      handleOnBlurField({
                        onBlur,
                        field: "feeTableLayout",
                        trigger,
                      });
                    }}
                    placeholder="Select"
                    className="w-full"
                  />
                  {getFormErrorMessage("feeTableLayout", errors)}
                </>
              )}
            />
          </div>
          {feeTableLayout === "chargeDescription" && (
            <div className="field col-4">
              <Label htmlFor="convenienceServiceFees">
                Convenience and service fees
              </Label>
              <Controller
                name="convenienceServiceFees"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    id="convenienceServiceFees"
                    options={CONVENIENCE_SERVICE_FEES_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    value={value}
                    onChange={onChange}
                    placeholder="Select"
                    className="w-full"
                  />
                )}
              />
            </div>
          )}
          <div className="field col-12 flex flex-column">
            <Label htmlFor="showDisclaimer">Show disclaimer</Label>
            <Controller
              name="showDisclaimer"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputSwitch
                  id="showDisclaimer"
                  checked={value ?? true}
                  onChange={onChange}
                />
              )}
            />
          </div>
          {showDisclaimer && (
            <div className="field col-12">
              <Label htmlFor="disclaimerType">Disclaimer content</Label>
              <Controller
                name="disclaimerType"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    id="disclaimerType"
                    options={DISCLAIMER_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    value={value}
                    onChange={onChange}
                    placeholder="Select"
                    className="w-full"
                  />
                )}
              />
            </div>
          )}
          <div className="field col-12 flex flex-column">
            <Label htmlFor="showTermsAndConditions">
              Show terms and conditions
            </Label>
            <Controller
              name="showTermsAndConditions"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputSwitch
                  id="showTermsAndConditions"
                  checked={value ?? true}
                  onChange={onChange}
                />
              )}
            />
          </div>
          {showTermsAndConditions && (
            <div className="field col-12">
              <Label htmlFor="termsAndConditionsText">
                Terms and conditions text
              </Label>
              <Controller
                name="termsAndConditionsText"
                control={control}
                rules={{
                  validate: (value, formValues) =>
                    formValues.showTermsAndConditions && !value?.trim()
                      ? "Terms and conditions text is required."
                      : true,
                }}
                render={({ field: { onChange, value, onBlur } }) => (
                  <>
                    <InputTextarea
                      id="termsAndConditionsText"
                      className="w-full"
                      value={value || ""}
                      rows={4}
                      placeholder="Enter terms and conditions. Use **text** for bold."
                      onChange={(e) => onChange(e.target.value)}
                      onBlur={() => {
                        handleOnBlurField({
                          onBlur,
                          field: "termsAndConditionsText",
                          trigger,
                        });
                      }}
                    />
                    <small className="text-color-secondary block mt-1">
                      Use **text** to make text bold (e.g. **important**)
                    </small>
                    {getFormErrorMessage("termsAndConditionsText", errors)}
                  </>
                )}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
